import CancelIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";
import { EMPLOYEE_BID_DATE_TYPE, EMPLOYEE_BID_STATUS } from "../../util/bid/bidConstants.js";
import { sortOnCaseSensitiveTexts } from "../../util/commonUtils";
import { THREE_YEARS_IN_PAST } from "../../util/dateHelper.js";
import { convert_Luxon_To_DateFns } from "../../util/dateTimeFormatter.js";
import {
  BID_TYPE_FILTER_MUST_BE_SELECTED,
  DATE_TYPE_FILTER_MUST_BE_SELECTED,
  STATUS_FILTER_MUST_BE_SELECTED,
} from "../../util/messageConstants.js";
import StarsDatePicker from "../fundamentals/StarsDatePicker.jsx";
import StarsSelectInput from "../fundamentals/StarsSelectInput.jsx";

const useStyles = makeStyles((theme) => ({
  filterInputs: {
    width: "100%",
    textAlign: "left",
  },
  topRow: {
    marginTop: "5px",
  },
  title: {
    fontSize: "30px",
    textAlign: "center",
    width: "200px",
    margin: "auto",
    marginTop: "25px",
    whiteSpace: "pre-line",
  },
}));

const BidEmployeeFilterForm = (props) => {
  const classes = useStyles();

  const SearchButton = () => {
    return (
      <IconButton
        size="small"
        className="search-icon-button"
        onMouseDown={props.onSearchSubmit.bind(this, props.searchString)}
      >
        <SearchIcon />
      </IconButton>
    );
  };

  const CancelIconButton = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={props.onSearchCancel.bind(this)}>
        <CancelIcon />
      </IconButton>
    );
  };

  const renderDateTypeSelectList = () => {
    return (
      <StarsSelectInput
        label="Date Type"
        menuMessage="Filter on Date Type"
        value={props.dateTypeFilter}
        allOptions={Object.values(EMPLOYEE_BID_DATE_TYPE)}
        sortComparator={sortOnCaseSensitiveTexts}
        onChange={props.onDateTypeFilterChange.bind(this)}
        errors={props.dateTypeFilter === "" ? DATE_TYPE_FILTER_MUST_BE_SELECTED : ""}
        className={classes.filterInputs}
        fullWidth={true}
      />
    );
  };

  const renderBidTypeSelectList = () => {
    return (
      <StarsSelectInput
        label="Bid Type"
        id="bidTypeSelectElement"
        menuMessage="Filter on Bid Type"
        value={props.bidTypeFilters}
        allOptions={Object.values(props.allBidTypesFilters)}
        sortComparator={sortOnCaseSensitiveTexts}
        errors={props.bidTypeFilters?.length <= 0 ? BID_TYPE_FILTER_MUST_BE_SELECTED : ""}
        className={classes.inputSelect}
        onChange={props.onBidTypeFilterChange.bind(this)}
        multiple={true}
        fullWidth={true}
      />
    );
  };

  const renderStartDatePicker = () => {
    return (
      <StarsDatePicker
        label="Start Date"
        date={props.startDateFilter}
        onChange={props.onStartDateFilterChange}
        dateErrors={props.startDateFilterErrors}
        minDate={convert_Luxon_To_DateFns(THREE_YEARS_IN_PAST(props.stationFilter))}
        className={classes.filterInputs}
        fullWidth={true}
      />
    );
  };

  const renderEndDatePicker = () => {
    return (
      <StarsDatePicker
        label="End Date"
        date={props.endDateFilter}
        onChange={props.onEndDateFilterChange}
        dateErrors={props.endDateFilterErrors}
        minDate={convert_Luxon_To_DateFns(THREE_YEARS_IN_PAST(props.stationFilter))}
        className={classes.filterInputs}
        fullWidth={true}
      />
    );
  };

  const renderBidStatusSelectList = () => {
    return (
      <StarsSelectInput
        label="Status"
        id="bidStatusSelectElement"
        menuMessage="Filter on Status"
        value={props.bidStatusFilters}
        allOptions={Object.values(EMPLOYEE_BID_STATUS)}
        sortComparator={sortOnCaseSensitiveTexts}
        errors={props.bidStatusFilters?.length <= 0 ? STATUS_FILTER_MUST_BE_SELECTED : ""}
        className={classes.inputSelect}
        onChange={props.onStatusFilterChange.bind(this)}
        fullWidth={true}
        multiple={true}
      />
    );
  };

  const renderSearchBar = () => {
    return (
      <FormControl className={classes.filterInputs} fullWidth>
        <TextField
          label="Search"
          variant="outlined"
          value={props.searchString}
          onChange={props.onSearchChange.bind(this)}
          onBlur={props.onSearchBlur.bind(this)}
          InputProps={{
            endAdornment:
              props.searchString !== "" ? (
                <InputAdornment position="end">
                  <CancelIconButton onSearchCancel={props.onSearchCancel} />
                  <SearchButton onSearchSubmit={props.onSearchSubmit} searchString={props.searchString} />
                </InputAdornment>
              ) : (
                <InputAdornment position="end">
                  <SearchButton />
                </InputAdornment>
              ),
          }}
          onKeyUp={props.onSearchEnterKeyPressed.bind(this)}
          error={props.searchStringErrors !== ""}
          helperText={props.searchStringErrors}
        />
      </FormControl>
    );
  };

  return (
    <div>
      <Grid container className={classes.topRow} data-testid="bid-filter-form">
        <Grid item>
          <Typography sx={{ textAlign: "left", marginLeft: "2rem", marginTop: "10px", fontSize: "20px" }}>
            <Box>{props.title}</Box>
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={1}
        sx={{ paddingLeft: "2rem", paddingRight: "2rem", paddingTop: "1.5rem", paddingBottom: "1rem" }}
      >
        <Grid item xs={1.2}>
          <Box>{renderBidTypeSelectList()}</Box>
        </Grid>
        <Grid item xs={1.3}>
          <Box>{renderBidStatusSelectList()}</Box>
        </Grid>
        <Grid item xs={2.3}>
          <Box>{renderDateTypeSelectList()}</Box>
        </Grid>
        <Grid item xs={2.2}>
          <Box>{renderStartDatePicker()}</Box>
        </Grid>
        <Grid item xs={2.2}>
          <Box>{renderEndDatePicker()}</Box>
        </Grid>
        <Grid item xs={1.9}>
          <Box>{renderSearchBar()}</Box>
        </Grid>
      </Grid>
    </div>
  );
};

BidEmployeeFilterForm.propTypes = {
  title: PropTypes.string.isRequired,
  dateTypeFilter: PropTypes.string.isRequired,
  onDateTypeFilterChange: PropTypes.func.isRequired,
  bidStatusFilters: PropTypes.array.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  startDateFilter: PropTypes.object.isRequired,
  startDateFilterErrors: PropTypes.string.isRequired,
  onStartDateFilterChange: PropTypes.func.isRequired,
  endDateFilter: PropTypes.object.isRequired,
  endDateFilterErrors: PropTypes.string.isRequired,
  onEndDateFilterChange: PropTypes.func.isRequired,
  bidTypeFilters: PropTypes.array.isRequired,
  allBidTypesFilters: PropTypes.array.isRequired,
  onBidTypeFilterChange: PropTypes.func.isRequired,
  searchString: PropTypes.string.isRequired,
  searchStringErrors: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchBlur: PropTypes.func.isRequired,
  onSearchEnterKeyPressed: PropTypes.func.isRequired,
  onSearchCancel: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  stationFilter: PropTypes.string.isRequired,
};

export default BidEmployeeFilterForm;
